import {inject, Injectable, linkedSignal, signal} from "@angular/core";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {ActivatedRoute, Router} from "@angular/router";
import {MealPlanModelDTO} from "@menu-api";
import {RolePermission, SecurityService} from "@menu-security";
import {from, map, of, switchMap, tap} from "rxjs";
import {catchError} from "rxjs/operators";
import {isRoughPlan} from "../pages/rough-planning/rough-planning.routes";
import {MealPlanModelApiClient} from "./api/meal-plan-model-api-client";

export const CREATION_TYPE = {
    CREATE: {name: "create", translationKey: "general.stepper.create"},
    DUPLICATE: {name: "duplicate", translationKey: "general.stepper.duplicate"},
    EDIT: {name: "edit", translationKey: "general.stepper.edit"},
};

export type CreationType = (typeof CREATION_TYPE)[keyof typeof CREATION_TYPE];

@Injectable()
export class MealPlanModelCreationService {
    private readonly mealPlanModelApiClient = inject(MealPlanModelApiClient);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly securityService = inject(SecurityService);
    public readonly isRoughPlan = isRoughPlan();

    public readonly refreshTrigger = signal(false);

    public readonly mealPlanModel = toSignal(
        toObservable(this.refreshTrigger).pipe(
            switchMap(() => this.route.queryParamMap.pipe(
                map(params => params.get("modelId")),
                switchMap((id) => {
                    if (id === null) {
                        void this.router.navigate(["../.."], {relativeTo: this.route});
                        return of(null);
                    }
                    return from(this.mealPlanModelApiClient.findMealPlanModel(id));
                }),
                tap(model => {
                    if (model != null) {
                        if (!this.securityService.hasPermissions([RolePermission.MealPlanModelWrite]) && !(this.securityService.hasPermissions([RolePermission.AssignedMealPlanModelEdit]) && this.securityService.isAssigned(model.assignedUsers))) {
                            void this.router.navigate(["../.."], {relativeTo: this.route});
                            return of(null);
                        }
                    }
                    return of(model);
                }),
                catchError(() => {
                    void this.router.navigate(["../.."], {relativeTo: this.route});
                    return of(null);
                }),
            )),
        ),
        {initialValue: null},
    );

    public readonly modifiedMealPlanModel = linkedSignal(this.mealPlanModel);
    public readonly stepsValidity = signal<boolean[]>([
        this.getCreationType() !== CREATION_TYPE.CREATE, // general information
        this.getCreationType() !== CREATION_TYPE.CREATE, // menu lines
        this.getCreationType() !== CREATION_TYPE.CREATE, // menu plans
        this.getCreationType() !== CREATION_TYPE.CREATE, // template plans
        this.getCreationType() !== CREATION_TYPE.CREATE, // KPI goals
    ]);

    public getCreationType(): CreationType | undefined {
        const mode = this.route.snapshot.queryParamMap.get("mode");
        return Object.values(CREATION_TYPE).find(t => t.name === mode);
    }

    public updateModelField<T extends keyof MealPlanModelDTO>(
        key: T,
        value: MealPlanModelDTO[T],
    ): void {
        if (this.modifiedMealPlanModel()?.[key] === value) return;
        this.modifiedMealPlanModel.update((m) => m == null ? null : {...m, [key]: value});
    }

    public updateStepValidity(index: number, valid: boolean): void {
        this.stepsValidity.update(validities => {
            const newValidities = [...validities];
            newValidities[index] = valid;
            return newValidities;
        });
    }

    public async save(draft?: boolean): Promise<void> {
        const model = this.mealPlanModel();
        const needsApproval = !this.securityService.hasPermissions([RolePermission.MealPlanModelWrite]);
        if (model) {
            this.updateModelField("draft", draft ?? model.draft);
            this.updateModelField("approved", needsApproval ? false : model.approved);
            this.updateModelField("rough", this.isRoughPlan());
            const modified = this.modifiedMealPlanModel();
            if (modified != null && model !== modified) {
                await this.mealPlanModelApiClient.updateMealPlanModel(modified);
            }
        }
        this.refreshTrigger.update(v => !v);
    }

    public async approve(): Promise<void> {
        const model = this.modifiedMealPlanModel();
        if (model) {
            await this.mealPlanModelApiClient.approveMealPlanModel(model.id);
            this.refreshTrigger.update(v => !v);
        }
    }
}
